/**
 * render_display_elements/display_media_elements.jsx
 *
 * 涵蓋媒體、圖表、特殊呈現類型：
 * photo, qrcode, location, bar_chart, pie_chart, line_chart,
 * slider, star_rating, data_table, signature, upload_file, upload_image
 */

import React from "react"
import { QRCodeSVG } from "qrcode.react"
import {
    Badge, ImageFileId, ColumnChart, PieBasicChart, LineBasicChart,
    DataTable, UploadImg, Signature, UploadFile, LucideIcon
} from "../../ui/ot"
import { FieldWrapper, ElementShell } from "../canvas_field_wrapper"
import StarRating from "../star.jsx"
import Slider from "../slider.jsx"

/** photo */
export const PhotoElement = ({ element }) => {
    let src = element?.src || element?.content || ""
    return (<div className="w-full h-full flex items-center justify-center pointer-events-auto overflow-hidden rounded">
        <ImageFileId
            className="w-full h-full object-cover"
            customize={true}
            src={src || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRKFSgdhQvBlZO6I8s-jtKIYOED1NqEs4xEjA&s"}
            file_id={element?.file_id}
        />
    </div>)
}

/** qrcode */
export const QRCodeElement = ({ element }) => {
    const size = Math.min(Number(element?.width), Number(element?.height))
    return (
        <div className="w-full h-full flex items-center justify-center pointer-events-auto">
            <QRCodeSVG value={element?.content ?? ""} size={size} />
        </div>
    )
}

/** location (google map / openstreet) */
export const LocationElement = ({ element }) => {
    const w = "100%"
    const h = Number(element?.style?.height ?? element?.height ?? 300)
    const mapSrc = element?.location_type === "openstreet"
        ? "https://www.openstreetmap.org/export/embed.html?bbox=120.31672954559328%2C22.62197342974718%2C120.3260636329651%2C22.629004656215855&layer=mapnik"
        : "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3682.748496453118!2d120.31813087499276!3d22.625865131016567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x346e04bd7ed51ff5%3A0xd343c4e121c2f6b5!2z5ZyL56uL6auY6ZuE5bir56-E5aSn5a245ZKM5bmz5qCh5Y2A!5e0!3m2!1szh-TW!2stw!4v1751445667962!5m2!1szh-TW!2stw"

    return (
        <div className="w-full pointer-events-auto overflow-hidden rounded-lg">
            <iframe
                src={mapSrc}
                width={w}
                height={h}
                style={{ border: "1px solid #e5e7eb", borderRadius: 8 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="地圖"
            />
        </div>
    )
}

/** slider */
export const SliderElement = ({ element, form_form_column_data_first, isFillMode }) => {
    const min = Number(form_form_column_data_first?.min_value ?? 0)
    const max = Number(form_form_column_data_first?.max_value ?? 100)
    const step = Number(form_form_column_data_first?.step ?? 1)
    const thickness = Number(form_form_column_data_first?.thickness ?? 6)
    const value = Number(element?.content ?? min)

    return (
        <ElementShell isFillMode={isFillMode}>
            <FieldWrapper
                label={form_form_column_data_first?.code}
                helpText={form_form_column_data_first?.help_text}
            >
                <Slider
                    value={value}
                    min={min}
                    max={max}
                    step={step}
                    thickness={thickness}
                    disabled
                    showInput={false}
                />
            </FieldWrapper>
        </ElementShell>
    )
}

/** star_rating */
export const StarRatingElement = ({ element, form_form_column_data_first, isFillMode }) => {
    const max = Number(form_form_column_data_first?.max_value ?? 5)
    const value = Number(element?.content ?? 0)

    return (
        <ElementShell isFillMode={isFillMode}>
            <FieldWrapper
                label={form_form_column_data_first?.code}
                helpText={form_form_column_data_first?.help_text}
            >
                <StarRating value={value} max={max} readOnly />
            </FieldWrapper>
        </ElementShell>
    )
}

/** upload_image */
export const UploadImageElement = ({ element, form_form_column_data_first, isFillMode }) => (
    <ElementShell isFillMode={isFillMode}>
        <FieldWrapper
            label={form_form_column_data_first?.code}
            required={form_form_column_data_first?.is_required === "1"}
            helpText={form_form_column_data_first?.help_text}
        >
            <UploadImg disabled value={element?.content} />
        </FieldWrapper>
    </ElementShell>
)

/** upload_file */
export const UploadFileElement = ({ element, form_form_column_data_first, isFillMode }) => (
    <ElementShell isFillMode={isFillMode}>
        <FieldWrapper
            label={form_form_column_data_first?.code}
            required={form_form_column_data_first?.is_required === "1"}
            helpText={form_form_column_data_first?.help_text}
        >
            <UploadFile disabled value={element?.content} />
        </FieldWrapper>
    </ElementShell>
)

/** signature */
export const SignatureElement = ({ element, form_form_column_data_first, isFillMode }) => (
    <ElementShell isFillMode={isFillMode}>
        <FieldWrapper
            label={form_form_column_data_first?.code}
            helpText={form_form_column_data_first?.help_text}
        >
            <Signature disabled value={element?.content} />
        </FieldWrapper>
    </ElementShell>
)

/** 共用圖表外殼 */
const ChartShell = ({ label, isFillMode, children }) => (
    <ElementShell isFillMode={isFillMode}>
        <FieldWrapper isFillMode={isFillMode} label={label}>
            <div className="w-full min-h-[180px]">
                {children}
            </div>
        </FieldWrapper>
    </ElementShell>
)

/** bar_chart */
export const BarChartElement = ({ element, form_form_column_data_first, chartData, isFillMode }) => (
    <ChartShell label={form_form_column_data_first?.code} isFillMode={isFillMode}>
        <ColumnChart
            chartConfig={{
                [form_form_column_data_first?.default_value?.yAxis ?? "value"]: {
                    label: form_form_column_data_first?.default_value?.yAxis ?? "value",
                    color: "hsl(var(--chart-1))",
                },
            }}
            chartData={chartData?.length ? chartData : form_form_column_data_first?.default_value?.data}
            xDataKeyArr={[form_form_column_data_first?.default_value?.xAxis ?? "name"]}
            yDataKeyArr={[form_form_column_data_first?.default_value?.yAxis ?? "value"]}
            showBrush={false}
        />
    </ChartShell>
)

/** pie_chart */
export const PieChartElement = ({ element, form_form_column_data_first, chartData, isFillMode }) => {
    const data = chartData?.length ? chartData : (form_form_column_data_first?.default_value?.data ?? [])
    const xAxis = form_form_column_data_first?.default_value?.xAxis ?? "name"
    const yAxis = form_form_column_data_first?.default_value?.yAxis ?? "value"
    const chartConfig = data.reduce((acc, item, i) => {
        acc[item?.[xAxis]] = { label: item?.[xAxis], color: `hsl(var(--chart-${i + 1}))` }
        return acc
    }, {})

    return (
        <ChartShell label={form_form_column_data_first?.code} isFillMode={isFillMode}>
            <PieBasicChart
                chartConfig={chartConfig}
                chartData={data}
                xDataKey={xAxis}
                yDataKey={yAxis}
            />
        </ChartShell>
    )
}

/** line_chart */
export const LineChartElement = ({ element, form_form_column_data_first, chartData, isFillMode }) => (
    <ChartShell label={form_form_column_data_first?.code} isFillMode={isFillMode}>
        <LineBasicChart
            chartConfig={{
                [form_form_column_data_first?.default_value?.yAxis ?? "value"]: {
                    label: form_form_column_data_first?.default_value?.yAxis ?? "value",
                    color: "hsl(var(--chart-1))",
                },
            }}
            chartData={chartData?.length ? chartData : form_form_column_data_first?.default_value?.data}
            xDataKeyArr={[form_form_column_data_first?.default_value?.xAxis ?? "name"]}
            yDataKeyArr={[form_form_column_data_first?.default_value?.yAxis ?? "value"]}
        />
    </ChartShell>
)

/** data_table */
export const DataTableElement = ({ element, form_form_column_data_first, chartData, isFillMode }) => (
    <ChartShell label={form_form_column_data_first?.code} isFillMode={isFillMode}>
        <DataTable
            data={chartData?.length ? chartData : (form_form_column_data_first?.default_value?.data ?? [])}
            columns={form_form_column_data_first?.default_value?.columns ?? []}
        />
    </ChartShell>
)
